/**
 * Two ways to reach Loupe, one interface.
 *
 * `frame` is the original: the app sits inside Loupe's shell and the two talk
 * by postMessage. `socket` is what Q10 added: the app is its own top-level
 * window - so the browser keeps its session cookie - and the two talk through
 * the server.
 *
 * Everything above this file is written against `Transport` and does not know
 * which one it got.
 */

import type { Envelope, SdkMessage, ServerMessage, ShellMessage } from "./protocol";

export interface Transport {
  readonly kind: "frame" | "socket";
  send(message: SdkMessage): void;
  onCommand(handler: (message: ShellMessage) => void): void;
  /** True once the other half is actually reachable. */
  ready(): boolean;
}

/**
 * `loupeOrigin` is the one origin a shell may speak from (D-053). Until
 * 2026-10-14 any page that framed the application could send `loupe:hello`,
 * become the shell, and receive the serialized page from `start-record` /
 * `stop-record` - without the person in front of it clicking anything. The
 * reviewer is trusted and checked by reproduction; a third party framing the
 * application is not a reviewer, and the channel is what keeps it out.
 */
export function frameTransport(loupeOrigin: string): Transport {
  let shell: { window: Window; origin: string } | null = null;
  let handler: ((message: ShellMessage) => void) | null = null;

  window.addEventListener("message", (event: MessageEvent<ShellMessage>) => {
    if (event.origin !== loupeOrigin) return;
    const data = event.data;
    if (!data || typeof data !== "object" || !String(data.type ?? "").startsWith("loupe:")) return;
    if (data.type === "loupe:hello") {
      shell = { window: event.source as Window, origin: event.origin };
    }
    if (!shell || event.source !== shell.window) return;
    handler?.(data);
  });

  return {
    kind: "frame",
    ready: () => shell !== null,
    send: (message) => shell?.window.postMessage(message, shell.origin),
    onCommand: (fn) => {
      handler = fn;
    },
  };
}

/**
 * What the socket is doing, for the bar in the application's window ([F-014]).
 * `restarting` is D-024: Loupe said it was stopping before the socket closed.
 * `lost` is every other close - the network, a proxy, a Loupe that died.
 */
export type SocketState = "connected" | "restarting" | "lost";

export function socketTransport(options: {
  origin: string;
  connection: string;
  token: string;
  onOpen?: () => void;
  onState?: (state: SocketState) => void;
}): Transport {
  const { origin, connection, token } = options;
  const url = `${origin.replace(/^http/, "ws")}/ws?connection=${encodeURIComponent(connection)}&role=agent&token=${encodeURIComponent(token)}`;

  let socket: WebSocket | null = null;
  let handler: ((message: ShellMessage) => void) | null = null;
  let backoff = 500;
  /** Loupe announced its restart; every close until the next open is that restart. */
  let restarting = false;
  const queue: SdkMessage[] = [];

  function flush(): void {
    while (socket?.readyState === WebSocket.OPEN && queue.length > 0) {
      socket.send(JSON.stringify(queue.shift()));
    }
  }

  function connect(): void {
    socket = new WebSocket(url);

    socket.addEventListener("open", () => {
      backoff = 500;
      restarting = false;
      options.onState?.("connected");
      flush();
      options.onOpen?.();
    });

    socket.addEventListener("message", (event) => {
      try {
        const envelope = JSON.parse(String(event.data)) as Envelope;
        // D-024: the one server message the agent itself reads.
        if (envelope.from === "server" && (envelope.payload as ServerMessage).type === "loupe:server-restart") {
          restarting = true;
          // Retry soon: a swapped container is back in seconds, and the first
          // attempts while it is down cost nothing.
          backoff = 500;
          options.onState?.("restarting");
          return;
        }
        // A room carries the server's own traffic too; the agent only acts on
        // what a dashboard told it to do.
        if (envelope.from !== "dashboard") return;
        handler?.(envelope.payload as ShellMessage);
      } catch {
        /* a malformed frame is not worth tearing the review down */
      }
    });

    // The reviewer is mid-review and the network is somebody else's problem;
    // reconnect rather than making them reload the application. While Loupe is
    // restarting every attempt fails the same way, and it is still the restart.
    socket.addEventListener("close", () => {
      options.onState?.(restarting ? "restarting" : "lost");
      window.setTimeout(connect, backoff);
      backoff = Math.min(backoff * 2, restarting ? 4000 : 15000);
    });
  }

  connect();

  return {
    kind: "socket",
    ready: () => socket?.readyState === WebSocket.OPEN,
    send: (message) => {
      queue.push(message);
      flush();
    },
    onCommand: (fn) => {
      handler = fn;
    },
  };
}
